import {
    AllowedEmployerInvestmentAccountTypes,
    AllowedInvestmentAccountTypes,
    EmployerPayer,
    EmployerSuspension,
    FeeCollectionType,
    FeeCollectionModel,
    FeeCollectionTypeState,
    FetchState,
    InvestmentFeeSchedule,
    InvestmentFeeScheduleState,
    InvestmentThreshold,
    ListParams,
} from "@/shared/types";
import { CensusManager, UpdateCensusManagerRequest } from "@/employer/types";
import {
    EbtoEmployerSearchResult,
    Employer,
    EmployerActivationRequest,
    EmployerListResult,
    EmployerMigrationPeriod,
    EmployerSearchResult,
    EmployerState,
    EmployerStatusChangeRequest,
    EmployerInvestmentEnrollmentTypeState,
} from "../types";
import * as userService from "@/shared/api/userService";
import * as bankService from "@/shared/api/bankService";
import * as billingService from "@/shared/api/billingService";
import * as drivewealthService from "@/shared/api/drivewealthService";

import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import * as storeHelper from "@/shared/storeHelper";
import { AxiosError } from "axios";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { serviceCallNotFoundWithDefault } from "@/shared/errorHelper";
import { DefaultFeeSchedules } from "@/admin/constants";

export const initialState: EmployerState = {
    employers: [],
    employerPages: [],
    employersFetchState: FetchState.Uninitialized,
    suspensionsFetch: FetchState.Uninitialized,
    suspensions: [],
    billing: {},
    employerFundingDays: {},
    employerTdaPlanId: {},
    investmentThreshold: {},
    investmentEnrollmentType: {},
    investmentFeeCollectionType: {},
    investmentFeeSchedules: {},
    censusManagerFetchState: FetchState.Uninitialized,
    pageNumber: 1,
    totalRecords: 0,
    employerSearchResult: null,
    employerSearchFetchState: FetchState.Uninitialized,
    ebtoEmployerSearchResult: null,
    ebtoEmployerSearchFetchState: FetchState.Uninitialized,
    employerMigrationPeriod: null,
    employerMigrationPeriodFetchState: FetchState.Uninitialized
};

export const useEmployerStore = defineStore("employer", {
    state: (): EmployerState => (_.cloneDeep(initialState)),
    getters: {
        fundingDays: state => state.employerFundingDays,
        getEmployersByPageNumber: state => (pageNumber: number): Employer[] => {
            const pageObject = state.employerPages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
        employerDetail: state => (employerId: number | string): { loaded: boolean, employer?: Employer } => {
            const numericEmployerId = typeof employerId === "string" ? parseInt(employerId) : employerId;
            const employer = state.employers.find(e => e.id === numericEmployerId);
            return employer ? { loaded: true, employer } : { loaded: false };
        },
        suspensionsLoaded: state => state.suspensionsFetch === FetchState.Loaded,
        suspensionForEmployerId: state => (employerId: any): EmployerSuspension => {
            const lookup = parseInt(employerId);
            return state.suspensions.find(s => s.employerId === lookup);
        },
        tdaPlanId: state => state.employerTdaPlanId,
        pageCount () {
            return this.totalRecords < this.pageSize ? 1 : Math.floor(
                (this.totalRecords + this.pageSize - 1) / this.pageSize);
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        employersLoaded: state => state.employersFetchState === FetchState.Loaded,
        employersFetchLoading: state => state.employersFetchState === FetchState.Loading,
        savedEmployerMigrationPeriod: state => state.employerMigrationPeriod,
        employerMigrationPeriodIsLoaded: state => state.employerMigrationPeriodFetchState === FetchState.Loaded,
        employerInvestmentFeeCollection: state => (employerId: number): FeeCollectionType => {
            const employerInvestmentFeeCollection = state.investmentFeeCollectionType[employerId];
            if (employerInvestmentFeeCollection) {
                return employerInvestmentFeeCollection.feeCollectionType;
            }
            return null;
        },
        isEmployerInvestmentFeeCollectionLoaded: state => (employerId: number): boolean => {
            const employerInvestmentFeeCollection = state.investmentFeeCollectionType[employerId];
            if (employerInvestmentFeeCollection && employerInvestmentFeeCollection.fetchState === FetchState.Loaded) {
                return true;
            }
            return false;
        },
        employerInvestmentFeeSchedules: state => (employerId: number): InvestmentFeeSchedule[] => {
            const investmentFeeSchedules = state.investmentFeeSchedules[employerId];
            if (investmentFeeSchedules) {
                return investmentFeeSchedules.feeSchedules;
            }
            return null;
        },
        isEmployerInvestmentFeeSchedulesLoaded: state => (employerId: number): boolean => {
            const investmentFeeSchedules = state.investmentFeeSchedules[employerId];
            if (investmentFeeSchedules && investmentFeeSchedules.fetchState === FetchState.Loaded) {
                return true;
            }
            return false;
        },
        isEmployerInvestmentEnrollmentTypeLoaded: state => (employerId: number): boolean => {
            const employerInvestmentEnrollmentType = state.investmentEnrollmentType[employerId];
            if (employerInvestmentEnrollmentType && employerInvestmentEnrollmentType.fetchState === FetchState.Loaded) {
                return true;
            }

            return false;
        },
        employerInvestmentEnrollmentType: state => (employerId: number): AllowedEmployerInvestmentAccountTypes => {
            const employerInvestmentEnrollmentType = state.investmentEnrollmentType[employerId];
            if (employerInvestmentEnrollmentType && employerInvestmentEnrollmentType.fetchState === FetchState.Loaded) {
                return employerInvestmentEnrollmentType.allowedEmployerInvestmentAccountTypes;
            }

            return null;
        },
        isPartnerInvestmentSettingsActive: state => (employerId: number): boolean => {
            const feeSchedules = state.investmentFeeSchedules[employerId];
            const feeCollection = state.investmentFeeCollectionType[employerId];

            // if default data is populated for employer investment settings, that indicates partner
            // settings are in use
            if (feeSchedules && feeCollection && feeCollection.isDefaultData && feeSchedules.isDefaultData) {
                return true;
            }
            return false;
        }
    },
    actions: {

        setEmployers (employersList: EmployerListResult) {
            for (const employer of employersList.employers) {
                employer.dateCreated = moment(employer.dateCreated).format("MM/DD/YYYY");
                if (employer.dateActivated) {
                    employer.dateActivated = moment(employer.dateActivated).format("MM/DD/YYYY");
                }
            }

            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== employersList.totalRecords) {
                this.employerPages = [];
            }

            const employerPages = [ ...this.employerPages ];
            employerPages[employersList.pageNumber] = employersList.employers;

            this.totalRecords = employersList.totalRecords;
            this.pageNumber = employersList.pageNumber;
            this.employerPages = employerPages;

            this.employers = this.employers.concat(employersList.employers);
        },
        setCensusManager (args: { employerId: number, censusManager: CensusManager }) {
            const e = this.employers.find(e => e.id === args.employerId);
            if (e) e.censusManager = args.censusManager;
        },
        setEmployerToActive (request: EmployerActivationRequest) {
            const index = this.employers.findIndex(e => e.id === request.employerId);
            const updateEmployer = {
                ...(this.employers[index]),
                isEmployerActivated: true,
                dateActivated: moment(new Date()).format("MM/DD/YYYY"),
                FundingOption: request.fundingOption
            };
            Vue.set(this.employers, index, updateEmployer);
        },
        setEmployerToTerminate (request: EmployerStatusChangeRequest) {
            const index = this.employers.findIndex(e => e.id === request.employerId);
            const updateEmployer = {
                ...(this.employers[index]),
                dateTerminationEffective: request.dateTerminationEffective
            };
            Vue.set(this.employers, index, updateEmployer);
        },
        setEmployerToReactivated (employerId: number) {
            const index = this.employers.findIndex(e => e.id === employerId);
            const updateEmployer = {
                ...(this.employers[index]),
                isTerminated: false
            };
            Vue.set(this.employers, index, updateEmployer);
        },
        setEmployer (employer: Employer) {
            const index = this.employers.findIndex(e => e.id === employer.id);

            if (index >= 0) {
                Vue.set(this.employers, index, employer);
            } else {
                this.employers = [ employer, ...this.employers ];
            }
        },
        setEmployerFundingDays (data: { employerId: number, days: number }) {
            Vue.set(this.employerFundingDays, data.employerId, data.days);
        },
        updateSuspensionsFetch ({ fetchState }: { fetchState: FetchState }) {
            this.suspensionsFetch = fetchState;
        },
        updateSuspensions (v) {
            this.suspensions = v.map(item => {
                const s: EmployerSuspension = {
                    employerId: item.employerId,
                    dateCreated: moment(item.dateCreated).format("MM/DD/YYYY"),
                    reason: item.reason,
                    isSuspended: item.isSuspended
                };
                return s;
            });
            this.suspensionsFetch = FetchState.Loaded;
        },
        setBilling ({ employerId, payload }: { employerId: number, payload: EmployerPayer }) {
            Vue.set(this.billing, employerId, payload);
        },
        setEmployerTdaPlanId (data: { employerId: number, tdaPlanId: string }) {
            Vue.set(this.employerTdaPlanId, data.employerId, data.tdaPlanId);
        },
        setEmployerInvestmentThreshold (data: { employerId: number, investmentThreshold: InvestmentThreshold }) {
            Vue.set(this.investmentThreshold, data.employerId, data.investmentThreshold);
        },
        setInvestmentFeeCollectionType (employerId: number, collectionType: FeeCollectionType) {
            const newState: FeeCollectionTypeState = {
                fetchState: FetchState.Loaded,
                feeCollectionType: collectionType,
                isDefaultData: !collectionType
            };
            Vue.set(this.investmentFeeCollectionType, employerId, newState);
        },
        clearInvestmentFeeCollectionTypeWithFetchState (employerId: number, fetchState: FetchState) {
            const newState: FeeCollectionTypeState = {
                fetchState,
                feeCollectionType: null,
                isDefaultData: false
            };
            Vue.set(this.investmentFeeCollectionType, employerId, newState);
        },
        setInvestmentFeeSchedules (employerId: number, feeSchedules: InvestmentFeeSchedule[]) {
            const newState: InvestmentFeeScheduleState = {
                feeSchedules: feeSchedules || _.cloneDeep(DefaultFeeSchedules),
                fetchState: FetchState.Loaded,
                isDefaultData: !feeSchedules
            };
            Vue.set(this.investmentFeeSchedules, employerId, newState);
        },
        clearInvestmentFeeScheduleWithFetchState (employerId: number, fetching: FetchState) {
            const newState: InvestmentFeeScheduleState = {
                fetchState: fetching, feeSchedules: [], isDefaultData: false
            };
            Vue.set(this.investmentFeeSchedules, employerId, newState);
        },
        setCensusManagerFetchState (fetchState: FetchState) {
            this.censusManagerFetchState = fetchState;
        },
        resetEmployerScheduledTermination (employerId: number) {
            const index = this.employers.findIndex(e => e.id === employerId);
            const updateEmployer = {
                ...(this.employers[index]),
                dateTerminationEffective: null
            };
            Vue.set(this.employers, index, updateEmployer);
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        clearEmployerPages () {
            this.employerPages = [];
            this.totalRecords = 0;
        },
        setEmployersFetchState (fetchState: FetchState) {
            this.employersFetchState = fetchState;
        },
        setEmployerInvestmentTypes (data: {
            employerId: number,
            investmentTypeState: EmployerInvestmentEnrollmentTypeState
        }) {
            Vue.set(this.investmentEnrollmentType, data.employerId, data.investmentTypeState);
        },
        setEmployerSearchFetchState (fetchState: FetchState) {
            this.employerSearchFetchState = fetchState;
        },
        setEmployerSearchResult (data: EmployerSearchResult) {
            this.employerSearchResult = data;
        },
        setEbtoEmployerSearchFetchState (fetchState: FetchState) {
            this.ebtoEmployerSearchFetchState = fetchState;
        },
        setEbtoEmployerSearchResult (data: EbtoEmployerSearchResult) {
            this.ebtoEmployerSearchResult = data;
  },
  setEmployerMigrationPeriodFetchState (fetchState: FetchState): void {
    this.employerMigrationPeriodFetchState = fetchState;
  },
  setEmployerMigrationPeriod (data: EmployerMigrationPeriod): void {
    this.employerMigrationPeriod = data;
        },
        getEmployerSuspensions ({ forceReload }: { forceReload?: boolean }) {
            if (forceReload) {
                this.updateSuspensionsFetch({ fetchState: FetchState.Uninitialized });
            }
            if (this.suspensionsFetch !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.updateSuspensionsFetch({ fetchState: FetchState.Loading });
            return bankService.employerSuspension()
                .then(resp => resp.data)
                .then(data => this.updateSuspensions(data));
        },
        getEmployerSearch ({
                               id, pageNumber, sortOption, clearState, searchTerm, partnerId, includeTerminated
                           }: ListParams) {
            this.setEmployerSearchFetchState(FetchState.Loading);
            const pageSizeStore = usePageSizeStore();
            return userService.getEmployerSearch(pageNumber, pageSizeStore.pageSize, searchTerm, partnerId,
                    includeTerminated)
                .then(response => {
                    this.setEmployerSearchResult(response.data);
                    this.setEmployerSearchFetchState(FetchState.Loaded);
                }).catch(err => {
                this.setEmployerSearchFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadEmployerFundingDays (employerId: number) {
            if (this.employerFundingDays[employerId]) return Promise.resolve();
            return bankService.getEmployerFundingDays(employerId)
                .then(r => {
                    this.setEmployerFundingDays({ employerId, days: r.data });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        activateEmployer (request: EmployerActivationRequest) {
            return userService.activateEmployer(request).then(resp => {
                this.setEmployerToActive(request);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        terminateEmployer (request: EmployerStatusChangeRequest) {
            return userService.terminateEmployer(request).then(resp => {
                this.setEmployerToTerminate(request);
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("The employer termination will be processed.");
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        reactivateEmployer (request: EmployerStatusChangeRequest) {
            return userService.reactivateEmployer(request).then(resp => {
                this.setEmployerToReactivated(request.employerId);
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("The employer has been reactivated.");
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        cancelScheduledTermination (employerId: number) {
            return userService.cancelScheduledTermination(employerId).then(resp => {
                this.resetEmployerScheduledTermination(employerId);
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("The scheduled termination is canceled.");
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        updateEmployerSuspension (suspension: EmployerSuspension) {
            return bankService.updateEmployerSuspension(suspension)
                .then(resp => {
                    return this.getEmployerSuspensions({ forceReload: true });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadEmployerBilling (employerId) {
            if (this.billing[employerId]) {
                return Promise.resolve();
            }

            return billingService.getPayer(employerId)
                .then(payload => this.setBilling({ employerId, payload }))
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        updateEmployerBilling ({ employerId, payload }: { employerId: number, payload: EmployerPayer }) {
            return billingService.putPayer(employerId, payload)
                .then(_ => this.setBilling({ employerId, payload }))
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        updateEmployerFundingDays (data: { employerId: number, days: number }) {
            return bankService.updateEmployerFundingDays(data)
                .then(_ => this.setEmployerFundingDays(data))
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        loadEmployer (employerId: number): Promise<Employer> {
            const existingEmployer = _.find(this.employers, x => x.id === employerId);

            if (existingEmployer) {
                return Promise.resolve(existingEmployer);
            }

            return userService.getEmployerById(employerId)
                .then(r => {
                    this.setEmployer(r.data);
                    return Promise.resolve(r.data);
                })
                .catch((err: AxiosError | any) => {
                    const displayError = (err.response.status === 404) ?
                        `Employer with id ${employerId} not found` : err;

                    storeHelper.handleError({ error: displayError, rethrow: false });
                    return Promise.reject();
                });
        },
        updateEmployer (e: Employer) {
            return userService.putEmployerById(e.id, e)
                .then(resp => {
                    this.setEmployer(resp.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadEmployerTdaPlanId (employerId: number) {
            if (this.employerTdaPlanId[employerId]) return Promise.resolve();

            return bankService.getEmployerTdaPlanId(employerId)
                .then(r => {
                    this.setEmployerTdaPlanId({ employerId, tdaPlanId: r.data });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        updateEmployerTdaPlanId (data: { employerId: number, tdaPlanId: string }) {
           return bankService.updateEmployerTdaPlanId(data)
                .then(_ => this.setEmployerTdaPlanId(data))
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        loadEmployerInvestmentThreshold (employerId: number) {
            if (this.investmentThreshold[employerId]) return Promise.resolve();
            return bankService.getEmployerInvestmentThreshold(employerId)
                .then(r => {
                    this.setEmployerInvestmentThreshold({ employerId, investmentThreshold: r.data });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        updateEmployerInvestmentThreshold (data: { employerId: number, investmentThreshold: InvestmentThreshold }) {
            return bankService.setEmployerInvestmentThreshold(data)
                .then(_ => this.setEmployerInvestmentThreshold(data))
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        saveCensusManager (args: { employerId, censusManager }): Promise<void> {
            if (this.censusManagerFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setCensusManagerFetchState(FetchState.Loading);
            const request: UpdateCensusManagerRequest = { censusManager: args.censusManager };
            return userService.updateCensusManager(args.employerId, request)
                .then(response => {
                    if (response.status === 200) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("The update has been saved.");
                        this.setCensusManager(args);
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                })
                .finally(() => this.setCensusManagerFetchState(FetchState.Loaded));
        },
        loadEmployerInvestmentType (employerId: number) {
            const existing = this.investmentEnrollmentType[employerId];
            if (existing && existing.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve(this.investmentEnrollmentType);
            }
            const investmentTypeState: EmployerInvestmentEnrollmentTypeState = {
                fetchState: FetchState.Loading, allowedEmployerInvestmentAccountTypes: null
            };
            this.setEmployerInvestmentTypes({ employerId, investmentTypeState });
            return drivewealthService.getEmployerInvestmentTypes(employerId)
                .then(r => {
                    investmentTypeState.fetchState = FetchState.Loaded;
                    investmentTypeState.allowedEmployerInvestmentAccountTypes = r.data;
                    this.setEmployerInvestmentTypes({ employerId, investmentTypeState });
                })
                .catch(err => {
                    investmentTypeState.fetchState = FetchState.Uninitialized;
                    this.setEmployerInvestmentTypes({ employerId, investmentTypeState });
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        setEmployerInvestmentType (data: { employerId: number, investmentType: AllowedInvestmentAccountTypes }) {
           return drivewealthService.setEmployerInvestmentTypes(data.employerId, data.investmentType)
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        getEbtoEmployerSearch (searchTerm: string) {
            this.setEbtoEmployerSearchFetchState(FetchState.Loading);
            return userService.ebtoEmployerSearch(searchTerm)
                .then(response => {
                    this.setEbtoEmployerSearchResult(response.data);
                    this.setEbtoEmployerSearchFetchState(FetchState.Loaded);
                }).catch(err => {
                this.setEbtoEmployerSearchFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getEmployerMigrationPeriod (employerId: number) {
            return drivewealthService.getEmployerMigrationPeriod(employerId)
              .then(response => {
                this.setEmployerMigrationPeriod(response.data);
              })
              .catch(err => {
                this.setEmployerMigrationPeriod(null);
              });
        },
        saveEmployerMigrationPeriod (data: { employerId: number, request: EmployerMigrationPeriod }) {
            const notifierStore = useNotifierStore();
            return drivewealthService.saveEmployerMigrationPeriod(data.employerId, data.request)
              .then(response => {
                this.setEmployerMigrationPeriod( response.data);
                notifierStore.setSuccess("The employer migration period has been udpated");
              })
              .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        updateSsoUserSyncStatus (data: { employerId: number, ssoUserSyncStatus: boolean }) {
            return userService.updateEmployerSsoUserSyncStatus(data.employerId, {
                ssoUserSyncStatus: data.ssoUserSyncStatus
            }).then(() => {
                const index = this.employers.findIndex(e => e.id === data.employerId);
                if (index >= 0) {
                    const employer = this.employers[index];
                    const updatedEmployer = {
                        ...employer,
                        ssoUserSyncStatus: data.ssoUserSyncStatus
                    };

                    Vue.set(this.employers, index, updatedEmployer);
                }

                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("SSO settings have been saved");
            }).catch(error => {
                storeHelper.handleError({ error, rethrow: false });
            });
        },
        getEmployerInvestmentFeeCollectionType (employerId: number) {
            const existing = this.investmentFeeCollectionType[employerId];

            if (existing && existing.fetchState !== FetchState.Uninitialized && !existing.isDefaultData) {
                return Promise.resolve();
            }

            this.clearInvestmentFeeCollectionTypeWithFetchState(employerId, FetchState.Loading);

            return serviceCallNotFoundWithDefault(
                billingService.getEmployerFeeCollection(employerId),
                () => {
                    const defaultFeeCollectionModel: FeeCollectionModel = {
                        feeCollectionAccountType: null
                    };
                    return defaultFeeCollectionModel;
                })
                .then(data => {
                    this.setInvestmentFeeCollectionType(employerId, data.feeCollectionAccountType);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.clearInvestmentFeeCollectionTypeWithFetchState(employerId, FetchState.Uninitialized);
                });
        },
        updateEmployerInvestmentFeeCollectionType (employerId: number, feeCollectionType: FeeCollectionType) {
            return billingService.putEmployerFeeCollection(employerId, { feeCollectionAccountType: feeCollectionType })
                .then(() => {
                    // update store with new fee collection type for employer
                    this.setInvestmentFeeCollectionType(employerId, feeCollectionType);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        deleteEmployerInvestmentFeeCollectionType (employerId: number) {
            return billingService.deleteEmployerFeeCollection(employerId)
                .then(() => {
                    // reset fee collection to default for employer after removing active entry
                    this.setInvestmentFeeCollectionType(employerId, null);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getEmployerInvestmentFeeSchedules (employerId: number) {
            const existing = this.investmentFeeSchedules[employerId];

            if (existing && existing.fetchState !== FetchState.Uninitialized && !existing.isDefaultData) {
                return Promise.resolve();
            }

            this.clearInvestmentFeeScheduleWithFetchState(employerId, FetchState.Loading);
            return serviceCallNotFoundWithDefault(billingService.getEmployerInvestmentFeeSchedules(employerId),
                () => <InvestmentFeeSchedule[]> null)
                .then(data => {
                    this.setInvestmentFeeSchedules(employerId, data);
                }).catch(err => {
                    this.clearInvestmentFeeScheduleWithFetchState(employerId, FetchState.Uninitialized);
                    storeHelper.handleError({error: err, rethrow: false});
                });
        },
        updateEmployerInvestmentFeeSchedules (employerId: number, request: InvestmentFeeSchedule[]) {
            return billingService.updateEmployerInvestmentFeeSchedules(employerId, request)
                .then(() => {
                    this.setInvestmentFeeSchedules(employerId, request);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        deleteEmployerInvestmentFeeSchedules (employerId: number) {
            return billingService.deleteEmployerInvestmentFeeSchedules(employerId)
                .then(() => {
                    // reset fee schedules to default for employer after removing active entries
                    this.setInvestmentFeeSchedules(employerId, null);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getActiveEmployerFeeSchedules (employerId: number) {
          return userService.getActiveEmployerFeeSchedules(employerId)
            .then(response => response.data)
            .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        getEmployerInterestRates (employerId: number) {
          return userService.getEmployerInterestRates(employerId)
            .then(response => response.data)
            .catch(error => storeHelper.handleError({ error, rethrow: false }));
        }
    }
});
